import "./payment.css";
import { useLocation } from "react-router";
import { useEffect, useRef, useMemo } from "react";
import { Modal as BootstrapModal } from "bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { setCart } from "../../../../redux/slices/cartSlice";
import { getCart } from "../../../../api/modules/b2droppie.class";
import { updateUserData } from "../../../../redux/slices/authSlice";
import { syncUserData } from "../../../../services/userSync.service";
import { clearPayment } from "../../../../redux/slices/paymentSlice";
import { toggleReFetch } from "../../../../redux/slices/themeSlice";

function Payment({ paymentData }) {
  const dispatch = useDispatch();
  const modalRef = useRef(null);
  const iframeRef = useRef(null);
  const location = useLocation();
  const appUrl = window.location.host;
  const modalInstanceRef = useRef(null);
  const user = useSelector((state) => state?.auth?.user);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);

  useEffect(() => {
    if (!paymentData?.url || !modalRef.current) return;

    modalInstanceRef.current = new BootstrapModal(modalRef.current, {
      backdrop: "static",
      keyboard: false,
    });

    modalInstanceRef.current.show();

    return () => {
      if (!modalInstanceRef.current || !modalRef.current) return;

      const modalEl = modalRef.current;

      const cleanup = () => {
        modalInstanceRef.current?.dispose();
        modalInstanceRef.current = null;

        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";

        modalEl.removeEventListener("hidden.bs.modal", cleanup);
      };

      modalEl.addEventListener("hidden.bs.modal", cleanup);
      modalInstanceRef.current.hide();
    };
  }, [paymentData?.url]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;

    if (!iframe || !iframe.contentWindow) return;

    iframe.contentWindow.postMessage(
      {
        type: "request-global-config",
        config: {
          applePayEnabled: true,
          crossDomainName: window.location.host,
        },
      },
      "*",
    );
  };

  const fetchCartData = async (page = 1) => {
    try {
      const updated = await syncUserData();
      if (updated) {
        dispatch(updateUserData(updated));
      }

      const response = await getCart({ id: user?.id, page });
      const apiData = response?.data;

      const items = apiData?.data?.map((item) => ({
        ...item,
        stock: item.quantity,
        quantity: item.quantity,
      }));

      const itemsCount = items.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0,
      );

      const totalPrice = items.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );

      dispatch(
        setCart({
          items,
          currentPage: apiData.current_page,
          lastPage: apiData.last_page,
          perPage: apiData.per_page,
          total: apiData.total,
          links: apiData.links,
          itemsCount,
          totalPrice,
        }),
      );
    } catch (err) {
      console.log("fetchCartData error:", err);
    }
  };

  const handleClose = () => {
    if (!modalInstanceRef.current) return;
    dispatch(clearPayment());
    const modalEl = modalRef.current;

    const cleanup = async () => {
      if (location.pathname === "/myaccount/cart") {
        fetchCartData();
        dispatch(toggleReFetch("pendingWithdrawals"));
      }

      modalInstanceRef.current?.dispose();
      modalInstanceRef.current = null;

      document.body.classList.remove("modal-open");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";

      modalEl.removeEventListener("hidden.bs.modal", cleanup);
    };

    modalEl.addEventListener("hidden.bs.modal", cleanup);
    modalInstanceRef.current.hide();
  };

  const iframeSrc = useMemo(() => {
    if (!paymentData?.url) return "";

    const url = new URL(paymentData.url);
    url.searchParams.set("cross_domain_name", appUrl);

    return url.toString();
  }, [paymentData?.url, appUrl]);

  return (
    <div ref={modalRef} className="modal fade" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-lg payment-modal-dialog">
        <div
          className={`modal-content ${
            isDarkMode ? "bg-dark text-light" : "bg-white text-dark"
          }`}
        >
          <div
            className={`modal-body p-0 ${isDarkMode ? "bg-dark" : "bg-white"}`}
            style={{ height: "90vh" }}
          >
            <button
              type="button"
              className={`payment-close-btn ${
                isDarkMode ? "btn-close-white" : ""
              }`}
              onClick={handleClose}
            >
              &times;
            </button>

            <iframe
              ref={iframeRef}
              title="Payment Frame"
              src={iframeSrc}
              id="breeze-payment-page"
              onLoad={handleIframeLoad}
              allow="payment; camera; microphone"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Payment;
